/**
 * POC: programmatic emblem re-rendering test (WEB-2 redesign)
 * Delete this file + its route in App.tsx once a direction is chosen.
 */
import { useEffect } from 'react';

const INK = '#17212B';
const INK_DEEP = '#0E141B';
const VELLUM = '#F2E8D5';
const VELLUM_MUTED = '#B8AE99';
const RULE = '#3A5152';
const ACCENT = '#D9743A';

interface EmblemProps {
  size: number;
  variant?: 'flat' | 'metallic' | 'metallic-pressed';
  color?: string;
}

function PhonoLexEmblem({ size, variant = 'metallic-pressed', color = VELLUM_MUTED }: EmblemProps) {
  const filterId = `metallic-${variant}-${size}`;
  // Cartographic / cosmist hairline: render at the same visual pixel width
  // regardless of SVG size. vectorEffect="non-scaling-stroke" makes the
  // stroke ignore the viewBox-to-pixel scaling.
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      stroke={color}
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={
        variant === 'metallic-pressed'
          ? { filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.55)) drop-shadow(0 -0.5px 0 rgba(255,255,255,0.05))' }
          : undefined
      }
    >
      <defs>
        <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.4" result="blur" />
          <feSpecularLighting
            in="blur"
            surfaceScale="2"
            specularConstant="0.45"
            specularExponent="22"
            lightingColor={VELLUM}
            result="spec"
          >
            <feDistantLight azimuth="225" elevation="55" />
          </feSpecularLighting>
          <feComposite in="spec" in2="SourceAlpha" operator="in" result="lit" />
          <feMerge>
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="lit" />
          </feMerge>
        </filter>
      </defs>

      <g filter={variant === 'metallic' ? `url(#${filterId})` : undefined} vectorEffect="non-scaling-stroke">
        {/* Top bracket — bar at top, ticks point DOWN toward nucleus */}
        <path d="M30 30 L30 22 L70 22 L70 30" vectorEffect="non-scaling-stroke" />
        {/* Bottom bracket — bar at bottom, ticks point UP toward nucleus */}
        <path d="M30 70 L30 78 L70 78 L70 70" vectorEffect="non-scaling-stroke" />
        {/* Nucleus */}
        <circle cx="50" cy="50" r="11" vectorEffect="non-scaling-stroke" />
        {/* Onset — cups LEFT (bulges toward nucleus, opens away from it) */}
        <path d="M20 38 A14 14 0 0 1 20 62" vectorEffect="non-scaling-stroke" />
        {/* Coda — cups RIGHT (bulges toward nucleus, opens away from it) */}
        <path d="M80 38 A14 14 0 0 0 80 62" vectorEffect="non-scaling-stroke" />
        {/* Onset vertex → nucleus left edge */}
        <line x1="27" y1="50" x2="39" y2="50" vectorEffect="non-scaling-stroke" />
        {/* Nucleus right edge → coda vertex */}
        <line x1="61" y1="50" x2="73" y2="50" vectorEffect="non-scaling-stroke" />
      </g>
    </svg>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: '4rem' }}>
      <h2
        style={{
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: '0.75rem',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: VELLUM_MUTED,
          marginBottom: '0.5rem',
          fontWeight: 400,
        }}
      >
        {title}
      </h2>
      <div style={{ width: '2.5rem', height: '1px', background: RULE, marginBottom: '2rem' }} />
      {children}
    </section>
  );
}

export default function PocEmblemPage() {
  const sizes = [16, 24, 48, 96, 192];

  useEffect(() => {
    document.documentElement.classList.add('poc-emblem-active');
    return () => {
      document.documentElement.classList.remove('poc-emblem-active');
    };
  }, []);

  return (
    <>
      <style>{`
        html.poc-emblem-active,
        html.poc-emblem-active body {
          background: ${INK};
        }
        html.poc-emblem-active body > #root > div {
          background: transparent !important;
          color: ${VELLUM};
        }
        html.poc-emblem-active header { background: transparent !important; }
        .poc-ink-ground {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background:
            radial-gradient(ellipse 80% 60% at 50% 0%, ${INK} 0%, ${INK_DEEP} 100%);
        }
        .poc-grain {
          position: fixed; inset: 0; z-index: 1;
          pointer-events: none;
          opacity: 0.12;
          mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='256' height='256'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.95  0 0 0 0 0.91  0 0 0 0 0.83  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        }
        .poc-content { position: relative; z-index: 2; }
      `}</style>
      <div className="poc-ink-ground" />
      <div className="poc-grain" />

      <div
        className="poc-content"
        style={{
          minHeight: '100vh',
          padding: '4rem 2rem',
          color: VELLUM,
          fontFamily: 'IBM Plex Sans, system-ui, sans-serif',
          maxWidth: '900px',
          margin: '0 auto',
        }}
      >
        <header style={{ marginBottom: '4rem' }}>
          <p
            style={{
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: ACCENT,
              marginBottom: '0.5rem',
            }}
          >
            POC · WEB-2
          </p>
          <h1
            style={{
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: '1.5rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontWeight: 400,
              marginBottom: '1rem',
            }}
          >
            PhonoLex emblem — re-rendering test
          </h1>
          <p style={{ color: VELLUM_MUTED, lineHeight: 1.7, fontSize: '0.95rem' }}>
            Source logo lives at <span style={{ color: VELLUM }}>phonolex.com/favicon.png</span> on a teal{' '}
            <code style={{ color: ACCENT, fontFamily: 'IBM Plex Mono, monospace' }}>#1E5A5A</code> ground.
            Below: that mark redrawn as inline SVG against our proposed{' '}
            <code style={{ color: ACCENT, fontFamily: 'IBM Plex Mono, monospace' }}>#0E2A2B</code> ink
            ground, in vellum stroke, with three render variants.
          </p>
        </header>

        <Section title="Steel-teal ladder · same hue family, dialing toward matte">
          <p style={{ color: VELLUM_MUTED, fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
            Current teal on the left for reference. Each step right shifts hue toward blue (183° → 215°) and
            drops saturation (51% → 25%) — the "more matte / steelier" direction.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1px', background: RULE }}>
            {[
              { name: 'Current', hsl: '183 / 51 / 11', bg: '#0E2A2B' },
              { name: 'Steel · light', hsl: '195 / 40 / 12', bg: '#12252B' },
              { name: 'Steel · medium', hsl: '210 / 30 / 13', bg: '#17212B' },
              { name: 'Steel · deep', hsl: '215 / 25 / 14', bg: '#1B222D' },
            ].map((swatch) => (
              <div
                key={swatch.bg}
                style={{
                  background: swatch.bg,
                  padding: '2rem 1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem',
                  minHeight: '320px',
                }}
              >
                <p
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: VELLUM_MUTED,
                    margin: 0,
                  }}
                >
                  {swatch.name} · {swatch.hsl}
                </p>
                <p
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.65rem',
                    color: VELLUM_MUTED,
                    margin: 0,
                  }}
                >
                  {swatch.bg}
                </p>
                <div style={{ marginTop: '0.5rem' }}>
                  <PhonoLexEmblem size={64} variant="metallic-pressed" color={VELLUM} />
                </div>
                <p
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.7rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: VELLUM,
                    margin: '0.5rem 0 0',
                  }}
                >
                  PhonoLex
                </p>
                <p style={{ color: VELLUM_MUTED, fontSize: '0.8rem', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
                  Phonological analysis &amp; psycholinguistic data.
                </p>
                <span
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.7rem',
                    color: ACCENT,
                    marginTop: 'auto',
                  }}
                >
                  phonolex.com →
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Side by side · 96px">
          <div style={{ display: 'flex', gap: '3rem', alignItems: 'flex-end' }}>
            <figure style={{ margin: 0 }}>
              <div
                style={{
                  background: '#1E5A5A',
                  width: 192,
                  height: 192,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${RULE}`,
                }}
              >
                <img
                  src="https://phonolex.com/apple-touch-icon.png"
                  alt="Original PhonoLex logo"
                  width={120}
                  height={120}
                />
              </div>
              <figcaption
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.1em',
                  color: VELLUM_MUTED,
                  marginTop: '0.75rem',
                  textTransform: 'uppercase',
                }}
              >
                Original (raster, on brand teal)
              </figcaption>
            </figure>

            <figure style={{ margin: 0 }}>
              <div
                style={{
                  width: 192,
                  height: 192,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${RULE}`,
                }}
              >
                <PhonoLexEmblem size={120} variant="metallic-pressed" />
              </div>
              <figcaption
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.1em',
                  color: VELLUM_MUTED,
                  marginTop: '0.75rem',
                  textTransform: 'uppercase',
                }}
              >
                Re-rendered (inline SVG, metallic-pressed)
              </figcaption>
            </figure>
          </div>
        </Section>

        <Section title="Variant · flat (no filter)">
          <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-end' }}>
            {sizes.map((s) => (
              <div key={s} style={{ textAlign: 'center' }}>
                <PhonoLexEmblem size={s} variant="flat" />
                <div
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.65rem',
                    color: VELLUM_MUTED,
                    marginTop: '0.5rem',
                  }}
                >
                  {s}px
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Variant · metallic (SVG specular filter)">
          <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-end' }}>
            {sizes.map((s) => (
              <div key={s} style={{ textAlign: 'center' }}>
                <PhonoLexEmblem size={s} variant="metallic" />
                <div
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.65rem',
                    color: VELLUM_MUTED,
                    marginTop: '0.5rem',
                  }}
                >
                  {s}px
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Variant · metallic-pressed (CSS drop-shadow bevel)">
          <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-end' }}>
            {sizes.map((s) => (
              <div key={s} style={{ textAlign: 'center' }}>
                <PhonoLexEmblem size={s} variant="metallic-pressed" />
                <div
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.65rem',
                    color: VELLUM_MUTED,
                    marginTop: '0.5rem',
                  }}
                >
                  {s}px
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="In context · accent variant">
          <div style={{ display: 'flex', gap: '3rem', alignItems: 'center' }}>
            <PhonoLexEmblem size={64} variant="metallic-pressed" color={ACCENT} />
            <PhonoLexEmblem size={64} variant="metallic-pressed" color={VELLUM} />
            <PhonoLexEmblem size={64} variant="metallic-pressed" color={VELLUM_MUTED} />
          </div>
        </Section>

        <Section title="In context · flagship card mock">
          <a
            href="https://phonolex.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              border: `1px solid ${RULE}`,
              padding: '2rem',
              textDecoration: 'none',
              color: VELLUM,
            }}
          >
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
              <PhonoLexEmblem size={48} variant="metallic-pressed" />
              <div>
                <p
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.7rem',
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: VELLUM_MUTED,
                    marginBottom: '0.5rem',
                  }}
                >
                  Flagship product
                </p>
                <h3
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.95rem',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    fontWeight: 400,
                    marginBottom: '1rem',
                  }}
                >
                  PhonoLex
                </h3>
                <p style={{ color: VELLUM_MUTED, lineHeight: 1.7, fontSize: '0.9rem', margin: 0 }}>
                  Phonological analysis, governed language generation, and psycholinguistic data for
                  SLPs, linguists, and educators.
                </p>
                <p
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '0.75rem',
                    color: ACCENT,
                    marginTop: '1rem',
                  }}
                >
                  phonolex.com →
                </p>
              </div>
            </div>
          </a>
        </Section>
      </div>
    </>
  );
}
